import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { userApi, stripeConnectApi, roleApi, vanityApi, venueApi, calendarApi, fileApi, boostApi } from '../lib/api';
import { 
  isPushSupported, 
  getNotificationPermission, 
  subscribeToPush, 
  unsubscribeFromPush,
  isSubscribedToPush 
} from '../lib/pushNotifications';
import {
  User, Briefcase, Shield, Link as LinkIcon, Plus, X, Loader2,
  Camera, Instagram, Twitter, Youtube, Linkedin, Globe, ShieldCheck,
  BadgeCheck, ExternalLink, Bell, BellOff, CreditCard, DollarSign,
  ArrowLeftRight, Copy, CheckCircle, AlertCircle, Wallet, Building2,
  MapPin, Users, Calendar, Upload, Image, Eye, GripVertical, Zap, Rocket, Star, TrendingUp
} from 'lucide-react';
import ProfilePreview from '../components/ProfilePreview';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Image Component for drag-and-drop portfolio reordering
const SortableImage = ({ id, img, idx, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
  };

  const imageUrl = img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square rounded-xl overflow-hidden group border border-border ${isDragging ? 'ring-2 ring-primary shadow-lg' : ''}`}
    >
      <img 
        src={imageUrl} 
        alt={`Portfolio ${idx + 1}`} 
        className="w-full h-full object-cover" 
      />
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 p-1.5 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        title="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </div>
      {/* Remove Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(idx);
        }}
        className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
        data-testid={`remove-image-${idx}`}
      >
        <X className="h-4 w-4" />
      </button>
      {/* Position indicator */}
      {idx === 0 && (
        <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-primary text-white text-xs font-medium">
          Primary
        </div>
      )}
    </div>
  );
};

const Settings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updateUser, logout } = useAuth();
  
  // Get initial tab from URL or default to 'profile'
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  
  // Category-specific rates structure
  const [categoryDetails, setCategoryDetails] = useState({});
  
  const [profile, setProfile] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    location: user?.location || '',
    main_category: user?.main_category || '',
    additional_categories: user?.additional_categories || [],
    portfolio_images: user?.portfolio_images || [],
    portfolio_videos: user?.portfolio_videos || [],
    certifications: user?.certifications || [],
    social_links: user?.social_links || {},
    is_insured: user?.is_insured || false,
    cancellation_policy: user?.cancellation_policy || 'moderate'
  });

  const [newImage, setNewImage] = useState('');
  const [newVideo, setNewVideo] = useState('');
  const [newCert, setNewCert] = useState('');
  
  // Stripe Connect state
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectBalance, setConnectBalance] = useState({ pending: 0, available: 0 });
  
  // Role management state
  const [roles, setRoles] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(false);
  
  // Vanity URL state
  const [screenName, setScreenName] = useState(user?.screen_name || '');
  const [screenNameAvailable, setScreenNameAvailable] = useState(null);
  const [screenNameLoading, setScreenNameLoading] = useState(false);
  
  // Venue state
  const [venueData, setVenueData] = useState(null);
  const [venueLoading, setVenueLoading] = useState(false);
  const [venueMetadata, setVenueMetadata] = useState({ venue_types: [], amenities: [] });
  const [venueForm, setVenueForm] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    capacity: '',
    venue_type: 'other',
    amenities: [],
    hourly_rate: '',
    daily_rate: '',
    minimum_hours: '1',
    is_available: true,
    offers_discount: false,
    discount_percent: '',
    discount_description: ''
  });
  
  // Calendar event state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    location: '',
    event_type: 'event',
    attachments: [],
    is_public: false
  });
  
  // Profile preview state
  const [showProfilePreview, setShowProfilePreview] = useState(false);
  
  // Email preferences state
  const [emailPreferences, setEmailPreferences] = useState({
    booking_confirmations: true,
    booking_reminders: true,
    payment_receipts: true,
    review_requests: true,
    payout_notifications: true,
    marketing_emails: false
  });
  const [emailPrefsLoading, setEmailPrefsLoading] = useState(false);
  
  // Boost state
  const [boostStatus, setBoostStatus] = useState({ is_boosted: false, boost_details: null });
  const [boostLoading, setBoostLoading] = useState(false);

  useEffect(() => {
    loadCategories();
    checkPushStatus();
    loadRoles();
    loadConnectStatus();
    loadVenue();
    loadCalendarEvents();
    loadVenueMetadata();
    loadEmailPreferences();
    loadBoostStatus();
    checkBoostSuccess();
    setScreenName(user?.screen_name || '');
    // Initialize category details from user data
    if (user?.category_details) {
      setCategoryDetails(user.category_details);
    }
  }, [user]);
  
  // Check URL params for boost success/cancel
  const checkBoostSuccess = async () => {
    const params = new URLSearchParams(window.location.search);
    const boostResult = params.get('boost');
    const boostId = params.get('boost_id');
    
    if (boostResult === 'success' && boostId) {
      // Activate the boost
      try {
        await boostApi.activate(boostId);
        toast.success('Profile boost activated! Your profile is now promoted.');
        loadBoostStatus();
      } catch (error) {
        toast.error('Failed to activate boost');
      }
      // Clean URL
      window.history.replaceState({}, '', '/settings');
    } else if (boostResult === 'cancelled') {
      toast.info('Boost purchase was cancelled');
      window.history.replaceState({}, '', '/settings');
    }
  };
  
  const loadBoostStatus = async () => {
    try {
      const response = await boostApi.getStatus();
      setBoostStatus(response.data);
    } catch (error) {
      console.error('Failed to load boost status:', error);
    }
  };
  
  const handlePurchaseBoost = async (durationDays, boostType = 'standard') => {
    setBoostLoading(true);
    try {
      const response = await boostApi.purchase(durationDays, boostType);
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purchase boost');
    } finally {
      setBoostLoading(false);
    }
  };
  
  const loadEmailPreferences = async () => {
    try {
      const response = await userApi.getEmailPreferences();
      if (response.data) {
        setEmailPreferences(response.data);
      }
    } catch (error) {
      console.error('Failed to load email preferences:', error);
    }
  };
  
  const updateEmailPreference = async (key, value) => {
    setEmailPrefsLoading(true);
    try {
      const response = await userApi.updateEmailPreferences({ [key]: value });
      if (response.data) {
        setEmailPreferences(response.data);
        toast.success('Email preference updated');
      }
    } catch (error) {
      toast.error('Failed to update preference');
    } finally {
      setEmailPrefsLoading(false);
    }
  };
  
  const loadVenue = async () => {
    try {
      const response = await venueApi.getMy();
      if (response.data) {
        setVenueData(response.data);
        setVenueForm({
          name: response.data.name || '',
          description: response.data.description || '',
          address: response.data.address || '',
          city: response.data.city || '',
          state: response.data.state || '',
          zip_code: response.data.zip_code || '',
          capacity: response.data.capacity?.toString() || '',
          venue_type: response.data.venue_type || 'other',
          amenities: response.data.amenities || [],
          hourly_rate: response.data.hourly_rate?.toString() || '',
          daily_rate: response.data.daily_rate?.toString() || '',
          minimum_hours: response.data.minimum_hours?.toString() || '1',
          is_available: response.data.is_available ?? true,
          offers_discount: response.data.offers_discount || false,
          discount_percent: response.data.discount_percent?.toString() || '',
          discount_description: response.data.discount_description || ''
        });
      }
    } catch (error) {
      // No venue yet, that's ok
    }
  };
  
  const loadVenueMetadata = async () => {
    try {
      const response = await venueApi.getMetadata();
      setVenueMetadata(response.data);
    } catch (error) {
      console.error('Failed to load venue metadata');
    }
  };
  
  const loadCalendarEvents = async () => {
    try {
      const response = await calendarApi.getMyEvents();
      setCalendarEvents(response.data || []);
    } catch (error) {
      console.error('Failed to load calendar events');
    }
  };
  
  const handleSaveVenue = async () => {
    if (!venueForm.name || !venueForm.address) {
      toast.error('Please enter venue name and address');
      return;
    }
    
    setVenueLoading(true);
    try {
      const data = {
        ...venueForm,
        capacity: venueForm.capacity ? parseInt(venueForm.capacity) : null,
        hourly_rate: venueForm.hourly_rate ? parseFloat(venueForm.hourly_rate) : null,
        daily_rate: venueForm.daily_rate ? parseFloat(venueForm.daily_rate) : null,
        minimum_hours: parseInt(venueForm.minimum_hours) || 1,
        discount_percent: venueForm.discount_percent ? parseFloat(venueForm.discount_percent) : null
      };
      
      if (venueData) {
        await venueApi.update(venueData.venue_id, data);
        toast.success('Venue updated!');
      } else {
        const response = await venueApi.create(data);
        setVenueData(response.data);
        toast.success('Venue created!');
      }
      loadVenue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save venue');
    } finally {
      setVenueLoading(false);
    }
  };
  
  const handleCreateEvent = async () => {
    if (!eventForm.title || !eventForm.start_date) {
      toast.error('Please enter event title and date');
      return;
    }
    
    try {
      await calendarApi.createEvent(eventForm);
      toast.success('Event created!');
      setShowEventForm(false);
      setEventForm({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        location: '',
        event_type: 'event',
        attachments: [],
        is_public: false
      });
      loadCalendarEvents();
    } catch (error) {
      toast.error('Failed to create event');
    }
  };
  
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await calendarApi.deleteEvent(eventId);
      toast.success('Event deleted');
      loadCalendarEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };
  
  const toggleAmenity = (amenity) => {
    setVenueForm(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };
  
  const loadRoles = async () => {
    try {
      const response = await roleApi.getRoles();
      setRoles(response.data.roles || []);
      setActiveRole(response.data.active_role);
    } catch (error) {
      // Fallback for old users without roles
      const userType = user?.user_type;
      if (userType === 'talent') {
        setRoles(['bookee']);
        setActiveRole('bookee');
      } else if (userType === 'both') {
        setRoles(['booker', 'bookee']);
        setActiveRole(user?.active_role || 'bookee');
      } else {
        setRoles(['booker']);
        setActiveRole('booker');
      }
    }
  };
  
  const loadConnectStatus = async () => {
    if (!roles.includes('bookee') && user?.user_type !== 'talent' && user?.user_type !== 'both') {
      return;
    }
    try {
      const response = await stripeConnectApi.getStatus();
      setConnectStatus(response.data);
      if (response.data.status === 'active') {
        const balanceRes = await stripeConnectApi.getBalance();
        setConnectBalance(balanceRes.data);
      }
    } catch (error) {
      console.error('Failed to load connect status:', error);
    }
  };
  
  const handleSetupPayouts = async () => {
    setConnectLoading(true);
    try {
      // First create account if needed
      if (!connectStatus || connectStatus.status === 'not_connected') {
        await stripeConnectApi.createAccount();
      }
      
      // Get onboarding link
      const currentUrl = window.location.origin;
      const response = await stripeConnectApi.getOnboardingLink(
        `${currentUrl}/settings?connect=success`,
        `${currentUrl}/settings?connect=refresh`
      );
      
      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start payout setup');
      console.error(error);
    } finally {
      setConnectLoading(false);
    }
  };
  
  const handleViewStripeDashboard = async () => {
    try {
      const response = await stripeConnectApi.getDashboardLink();
      window.open(response.data.url, '_blank');
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };
  
  const handleAddRole = async (role) => {
    setRoleLoading(true);
    try {
      const response = await roleApi.addRole(role);
      setRoles(response.data.roles);
      setActiveRole(response.data.active_role);
      updateUser({ ...user, roles: response.data.roles, active_role: response.data.active_role, user_type: response.data.user_type });
      toast.success(`You are now also a ${role === 'bookee' ? 'Bookee (Talent)' : 'Booker (Client)'}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add role');
    } finally {
      setRoleLoading(false);
    }
  };
  
  const handleSwitchRole = async (role) => {
    setRoleLoading(true);
    try {
      const response = await roleApi.switchRole(role);
      setActiveRole(response.data.active_role);
      updateUser({ ...user, active_role: response.data.active_role });
      toast.success(`Switched to ${role === 'bookee' ? 'Bookee' : 'Booker'} view`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch role');
    } finally {
      setRoleLoading(false);
    }
  };
  
  const checkScreenName = async (name) => {
    if (!name || name.length < 3) {
      setScreenNameAvailable(null);
      return;
    }
    setScreenNameLoading(true);
    try {
      const response = await vanityApi.checkAvailability(name);
      setScreenNameAvailable(response.data);
    } catch (error) {
      setScreenNameAvailable(null);
    } finally {
      setScreenNameLoading(false);
    }
  };
  
  const handleUpdateScreenName = async () => {
    if (!screenNameAvailable?.available) {
      toast.error('Please choose an available screen name');
      return;
    }
    setScreenNameLoading(true);
    try {
      await vanityApi.updateScreenName(screenName);
      updateUser({ ...user, screen_name: screenName });
      toast.success('Screen name updated! Your profile is now at: ' + window.location.origin + '/' + screenName);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update screen name');
    } finally {
      setScreenNameLoading(false);
    }
  };
  
  const copyProfileLink = () => {
    const link = `${window.location.origin}/talent/${user?.screen_name || user?.user_id}`;
    navigator.clipboard.writeText(link);
    toast.success('Profile link copied!');
  };

  const checkPushStatus = async () => {
    setPushSupported(isPushSupported());
    setPushPermission(getNotificationPermission());
    if (isPushSupported()) {
      const subscribed = await isSubscribedToPush();
      setPushSubscribed(subscribed);
    }
  };

  const handleTogglePush = async () => {
    setPushLoading(true);
    try {
      if (pushSubscribed) {
        const result = await unsubscribeFromPush();
        if (result.success) {
          setPushSubscribed(false);
          toast.success('Push notifications disabled');
        } else {
          toast.error(result.error || 'Failed to unsubscribe');
        }
      } else {
        const result = await subscribeToPush();
        if (result.success) {
          setPushSubscribed(true);
          toast.success('Push notifications enabled! You\'ll receive alerts for new bookings, messages, and more.');
        } else {
          toast.error(result.error || 'Failed to enable notifications');
        }
      }
    } catch (error) {
      toast.error('Failed to update notification settings');
    } finally {
      setPushLoading(false);
      setPushPermission(getNotificationPermission());
    }
  };

  const loadCategories = async () => {
    try {
      const response = await userApi.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const dataToSave = {
        ...profile,
        category_details: categoryDetails
      };
      const response = await userApi.updateProfile(dataToSave);
      updateUser(response.data);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleBecomeTalent = async () => {
    try {
      await userApi.becomeTalent();
      updateUser({ ...user, user_type: 'talent' });
      toast.success('You are now a talent! Complete your profile to start getting bookings.');
    } catch (error) {
      toast.error('Failed to upgrade account');
    }
  };

  // Handle main category selection
  const handleMainCategoryChange = (cat) => {
    setProfile({ ...profile, main_category: cat });
    // Initialize category details if not exists
    if (!categoryDetails[cat]) {
      setCategoryDetails({
        ...categoryDetails,
        [cat]: { description: '', rate_type: 'hourly', hourly_rate: '', min_hours: '', flat_rate: '' }
      });
    }
  };

  // Handle additional category toggle
  const toggleAdditionalCategory = (cat) => {
    if (cat === profile.main_category) return; // Can't add main as additional
    
    const current = profile.additional_categories || [];
    if (current.includes(cat)) {
      // Remove
      setProfile({ ...profile, additional_categories: current.filter(c => c !== cat) });
      const newDetails = { ...categoryDetails };
      delete newDetails[cat];
      setCategoryDetails(newDetails);
    } else if (current.length < 3) {
      // Add (max 3)
      setProfile({ ...profile, additional_categories: [...current, cat] });
      // Initialize category details
      if (!categoryDetails[cat]) {
        setCategoryDetails({
          ...categoryDetails,
          [cat]: { description: '', rate_type: 'hourly', hourly_rate: '', min_hours: '', flat_rate: '' }
        });
      }
    } else {
      toast.error('Maximum 3 additional categories allowed');
    }
  };

  // Update category detail
  const updateCategoryDetail = (cat, field, value) => {
    setCategoryDetails({
      ...categoryDetails,
      [cat]: {
        ...categoryDetails[cat],
        [field]: value
      }
    });
  };

  // File upload handler for profile picture
  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max size: 5MB');
      return;
    }

    setLoading(true);
    try {
      const response = await fileApi.uploadProfilePicture(file);
      const imageUrl = response.data.url;
      
      // Update local state
      setProfile(prev => ({ ...prev, picture: imageUrl }));
      
      // Refresh user data
      await updateUser({ picture: imageUrl });
      toast.success('Profile picture updated!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload profile picture');
    } finally {
      setLoading(false);
    }
  };

  // File upload handler for portfolio images
  const handlePortfolioImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setLoading(true);
    let successCount = 0;

    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`Skipped ${file.name}: not an image`);
        continue;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`Skipped ${file.name}: file too large (max 10MB)`);
        continue;
      }

      try {
        const response = await fileApi.uploadPortfolio(file, 'image');
        const imageUrl = response.data.url;
        
        setProfile(prev => ({
          ...prev,
          portfolio_images: [...(prev.portfolio_images || []), imageUrl]
        }));
        successCount++;
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} image${successCount > 1 ? 's' : ''} added to portfolio`);
    }
    setLoading(false);
  };

  // File upload handler for certifications/other files
  const handleCertificationUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max size: 10MB');
      return;
    }

    setLoading(true);
    try {
      const response = await fileApi.uploadPortfolio(file, 'certification');
      const fileUrl = response.data.url;
      const fileName = response.data.file_name || file.name;
      
      // Store as object with name and url
      setProfile(prev => ({
        ...prev,
        certifications: [...(prev.certifications || []), fileName]
      }));
      toast.success('File added to portfolio');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  // Legacy handlers for URL-based additions (keep for flexibility)
  const handleImageUpload = async (e, setAsProfile = false) => {
    // Now handled by specific functions above
    if (setAsProfile) {
      await handleProfilePictureUpload(e);
    } else {
      await handlePortfolioImageUpload(e);
    }
  };

  // Drag-and-drop handler for portfolio image reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setProfile((prev) => {
        const oldIndex = prev.portfolio_images.findIndex((_, i) => `image-${i}` === active.id);
        const newIndex = prev.portfolio_images.findIndex((_, i) => `image-${i}` === over.id);
        
        const newImages = arrayMove(prev.portfolio_images, oldIndex, newIndex);
        toast.success('Portfolio order updated');
        return {
          ...prev,
          portfolio_images: newImages
        };
      });
    }
  };

  const handleRemoveImage = (idx) => {
    setProfile({
      ...profile,
      portfolio_images: profile.portfolio_images.filter((_, i) => i !== idx)
    });
  };

  // File upload handler for videos (URL only - videos are typically hosted externally)
  const addPortfolioVideo = () => {
    if (newVideo && !profile.portfolio_videos.includes(newVideo)) {
      setProfile({ ...profile, portfolio_videos: [...profile.portfolio_videos, newVideo] });
      setNewVideo('');
      toast.success('Video added to portfolio');
    }
  };

  // Legacy URL-based image add (keep for flexibility)
  const addPortfolioImage = () => {
    if (newImage && !profile.portfolio_images.includes(newImage)) {
      setProfile({ ...profile, portfolio_images: [...profile.portfolio_images, newImage] });
      setNewImage('');
    }
  };

  const addCertification = () => {
    if (newCert && !profile.certifications.includes(newCert)) {
      setProfile({ ...profile, certifications: [...profile.certifications, newCert] });
      setNewCert('');
    }
  };

  const updateSocialLink = (platform, url) => {
    setProfile({
      ...profile,
      social_links: { ...profile.social_links, [platform]: url }
    });
  };

  const isTalent = user?.user_type === 'talent';
  const allSelectedCategories = [profile.main_category, ...(profile.additional_categories || [])].filter(Boolean);

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="settings-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
            Settings
          </h1>
          <p className="text-muted-foreground">
            Manage your profile and account settings
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-muted/50 rounded-xl p-1 flex-wrap">
            <TabsTrigger value="profile" className="rounded-lg">
              <User className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
            {(roles.includes('bookee') || isTalent) && (
              <TabsTrigger value="portfolio" className="rounded-lg">
                <Camera className="h-4 w-4 mr-2" />
                Portfolio
              </TabsTrigger>
            )}
            <TabsTrigger value="social" className="rounded-lg">
              <LinkIcon className="h-4 w-4 mr-2" />
              Social
            </TabsTrigger>
            {(roles.includes('bookee') || isTalent) && (
              <TabsTrigger value="payouts" className="rounded-lg">
                <Wallet className="h-4 w-4 mr-2" />
                Payouts
              </TabsTrigger>
            )}
            {(roles.includes('bookee') || isTalent) && (
              <TabsTrigger value="boost" className="rounded-lg">
                <Zap className="h-4 w-4 mr-2" />
                Boost
              </TabsTrigger>
            )}
            <TabsTrigger value="notifications" className="rounded-lg">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="account" className="rounded-lg">
              <Shield className="h-4 w-4 mr-2" />
              Account
            </TabsTrigger>
            {roles.includes('venue') && (
              <TabsTrigger value="venue" className="rounded-lg">
                <Building2 className="h-4 w-4 mr-2" />
                Venue
              </TabsTrigger>
            )}
            {(roles.includes('bookee') || isTalent) && (
              <TabsTrigger value="calendar" className="rounded-lg">
                <Calendar className="h-4 w-4 mr-2" />
                Calendar
              </TabsTrigger>
            )}
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Picture Section */}
                <div className="flex items-center gap-6 p-4 rounded-xl bg-muted/30">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-muted border-4 border-background shadow-lg">
                      {profile.picture ? (
                        <img src={profile.picture} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-primary/20">
                          <User className="h-10 w-10 text-primary/60" />
                        </div>
                      )}
                    </div>
                    <label className="absolute -bottom-1 -right-1 p-2 rounded-full bg-primary text-white cursor-pointer hover:bg-primary/90 transition-colors shadow-lg">
                      <Camera className="h-4 w-4" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, true)}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <div>
                    <h3 className="font-semibold">Profile Picture</h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      This is your main photo that appears on your profile
                    </p>
                    <label className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 cursor-pointer">
                      <Upload className="h-4 w-4" />
                      <span>Upload new photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, true)}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                <Separator />

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={profile.name}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      className="rounded-xl"
                      data-testid="settings-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      placeholder="City, State"
                      value={profile.location}
                      onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                      className="rounded-xl"
                      data-testid="settings-location-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    placeholder="Tell us about yourself..."
                    value={profile.bio}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                    className="rounded-xl min-h-[100px]"
                    data-testid="settings-bio-input"
                  />
                </div>

                {isTalent && (
                  <>
                    {/* Categories Section - Moved under Bio */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Main Category *</Label>
                        <p className="text-sm text-muted-foreground mb-3">Select your primary talent category</p>
                        <Select value={profile.main_category} onValueChange={handleMainCategoryChange}>
                          <SelectTrigger className="rounded-xl">
                            <SelectValue placeholder="Select main category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(cat => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-base font-semibold">Additional Categories <span className="text-muted-foreground text-sm font-normal">(Optional)</span></Label>
                        <p className="text-sm text-muted-foreground mb-3">Select up to 3 more categories to appear in multiple searches</p>
                        
                        {/* Dropdown selectors for additional categories */}
                        <div className="space-y-3">
                          {/* Additional Category 1 */}
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground w-24">Additional 1:</span>
                            <Select 
                              value={profile.additional_categories?.[0] || '__none__'} 
                              onValueChange={(val) => {
                                const current = profile.additional_categories || [];
                                if (val === '__none__') {
                                  // Remove first additional, shift others up
                                  setProfile({ ...profile, additional_categories: current.slice(1) });
                                } else {
                                  const newCats = [val, ...current.filter((c, i) => i !== 0 && c !== val)].slice(0, 3);
                                  setProfile({ ...profile, additional_categories: newCats });
                                  if (!categoryDetails[val]) {
                                    setCategoryDetails({
                                      ...categoryDetails,
                                      [val]: { description: '', rate_type: 'hourly', hourly_rate: '', min_hours: '', flat_rate: '' }
                                    });
                                  }
                                }
                              }}
                            >
                              <SelectTrigger className="rounded-xl flex-1">
                                <SelectValue placeholder="Select category (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {categories
                                  .filter(c => c !== profile.main_category && c !== profile.additional_categories?.[1] && c !== profile.additional_categories?.[2])
                                  .sort((a, b) => a.localeCompare(b))
                                  .map(cat => (
                                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            {profile.additional_categories?.[0] && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const current = profile.additional_categories || [];
                                  setProfile({ ...profile, additional_categories: current.slice(1) });
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          
                          {/* Additional Category 2 */}
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground w-24">Additional 2:</span>
                            <Select 
                              value={profile.additional_categories?.[1] || '__none__'} 
                              onValueChange={(val) => {
                                const current = profile.additional_categories || [];
                                if (val === '__none__') {
                                  // Remove second additional, keep first, shift third up
                                  const newCats = [current[0], current[2]].filter(Boolean);
                                  setProfile({ ...profile, additional_categories: newCats });
                                } else {
                                  const first = current[0] || '';
                                  const third = current[2] || '';
                                  const newCats = [first, val, third].filter(Boolean);
                                  setProfile({ ...profile, additional_categories: newCats.filter((c, i, arr) => arr.indexOf(c) === i) });
                                  if (!categoryDetails[val]) {
                                    setCategoryDetails({
                                      ...categoryDetails,
                                      [val]: { description: '', rate_type: 'hourly', hourly_rate: '', min_hours: '', flat_rate: '' }
                                    });
                                  }
                                }
                              }}
                            >
                              <SelectTrigger className="rounded-xl flex-1">
                                <SelectValue placeholder="Select category (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {categories
                                  .filter(c => c !== profile.main_category && c !== profile.additional_categories?.[0] && c !== profile.additional_categories?.[2])
                                  .sort((a, b) => a.localeCompare(b))
                                  .map(cat => (
                                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            {profile.additional_categories?.[1] && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const current = profile.additional_categories || [];
                                  const newCats = [current[0], current[2]].filter(Boolean);
                                  setProfile({ ...profile, additional_categories: newCats });
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          
                          {/* Additional Category 3 */}
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground w-24">Additional 3:</span>
                            <Select 
                              value={profile.additional_categories?.[2] || '__none__'} 
                              onValueChange={(val) => {
                                const current = profile.additional_categories || [];
                                if (val === '__none__') {
                                  // Remove third additional
                                  setProfile({ ...profile, additional_categories: current.slice(0, 2) });
                                } else {
                                  const first = current[0] || '';
                                  const second = current[1] || '';
                                  const newCats = [first, second, val].filter(Boolean);
                                  setProfile({ ...profile, additional_categories: newCats.filter((c, i, arr) => arr.indexOf(c) === i) });
                                  if (!categoryDetails[val]) {
                                    setCategoryDetails({
                                      ...categoryDetails,
                                      [val]: { description: '', rate_type: 'hourly', hourly_rate: '', min_hours: '', flat_rate: '' }
                                    });
                                  }
                                }
                              }}
                            >
                              <SelectTrigger className="rounded-xl flex-1">
                                <SelectValue placeholder="Select category (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {categories
                                  .filter(c => c !== profile.main_category && c !== profile.additional_categories?.[0] && c !== profile.additional_categories?.[1])
                                  .sort((a, b) => a.localeCompare(b))
                                  .map(cat => (
                                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            {profile.additional_categories?.[2] && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const current = profile.additional_categories || [];
                                  setProfile({ ...profile, additional_categories: current.slice(0, 2) });
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Category Details - Description and Rates for each selected category */}
                    {allSelectedCategories.length > 0 && (
                      <div className="space-y-6">
                        <Separator />
                        <Label className="text-base font-semibold">Category Details & Rates</Label>
                        
                        {allSelectedCategories.map((cat, idx) => (
                          <Card key={cat} className={`${idx === 0 ? 'border-primary/50' : ''}`}>
                            <CardContent className="pt-4 space-y-4">
                              <div className="flex items-center gap-2">
                                <Badge className={idx === 0 ? 'bg-primary' : ''}>
                                  {idx === 0 ? 'Main' : `Additional ${idx}`}
                                </Badge>
                                <span className="font-semibold">{cat}</span>
                              </div>

                              {/* Description */}
                              <div className="space-y-2">
                                <Label>Brief Description <span className="text-muted-foreground text-xs">(Max 150 characters - helps with keyword searches)</span></Label>
                                <Textarea
                                  placeholder={`Describe your ${cat} services...`}
                                  value={categoryDetails[cat]?.description || ''}
                                  onChange={(e) => updateCategoryDetail(cat, 'description', e.target.value.slice(0, 150))}
                                  maxLength={150}
                                  className="rounded-xl min-h-[60px]"
                                />
                                <p className="text-xs text-muted-foreground text-right">
                                  {(categoryDetails[cat]?.description || '').length}/150
                                </p>
                              </div>

                              {/* Rate Type Selection */}
                              <div className="space-y-3">
                                <Label>Rate Type</Label>
                                <RadioGroup
                                  value={categoryDetails[cat]?.rate_type || 'hourly'}
                                  onValueChange={(value) => updateCategoryDetail(cat, 'rate_type', value)}
                                  className="flex gap-4"
                                >
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="hourly" id={`${cat}-hourly`} />
                                    <Label htmlFor={`${cat}-hourly`} className="cursor-pointer">Hourly Rate</Label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="flat" id={`${cat}-flat`} />
                                    <Label htmlFor={`${cat}-flat`} className="cursor-pointer">Flat Rate</Label>
                                  </div>
                                </RadioGroup>
                              </div>

                              {/* Rate Fields based on selection */}
                              {categoryDetails[cat]?.rate_type === 'hourly' ? (
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label>Hourly Rate ($)</Label>
                                    <Input
                                      type="number"
                                      placeholder="50"
                                      value={categoryDetails[cat]?.hourly_rate || ''}
                                      onChange={(e) => updateCategoryDetail(cat, 'hourly_rate', e.target.value)}
                                      className="rounded-xl"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Minimum Hours</Label>
                                    <Input
                                      type="number"
                                      placeholder="2"
                                      value={categoryDetails[cat]?.min_hours || ''}
                                      onChange={(e) => updateCategoryDetail(cat, 'min_hours', e.target.value)}
                                      className="rounded-xl"
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <Label>Flat Rate (Minimum) ($)</Label>
                                  <Input
                                    type="number"
                                    placeholder="200"
                                    value={categoryDetails[cat]?.flat_rate || ''}
                                    onChange={(e) => updateCategoryDetail(cat, 'flat_rate', e.target.value)}
                                    className="rounded-xl max-w-[200px]"
                                  />
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}

                    <Separator />

                    {/* Verified Identity Section - Links to Stripe Identity (future) */}
                    <div className={`p-4 rounded-xl border transition-colors ${user?.is_verified ? 'bg-blue-500/10 border-blue-500/30' : 'bg-muted/50 border-border'}`}>
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${user?.is_verified ? 'bg-blue-500/20' : 'bg-muted'}`}>
                          <BadgeCheck className={`h-5 w-5 ${user?.is_verified ? 'text-blue-400' : 'text-muted-foreground'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">Verified Identity</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {user?.is_verified 
                                  ? 'Your identity has been verified. An "ID Verified" badge is displayed on your profile.'
                                  : 'Verify your identity to display an "ID Verified" badge on your profile, building trust with clients.'}
                              </p>
                            </div>
                            {user?.is_verified ? (
                              <Badge className="bg-blue-500 text-white">
                                <BadgeCheck className="h-3 w-3 mr-1" />
                                Verified
                              </Badge>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-xl"
                                onClick={() => toast.info('ID verification coming soon! This will connect to Stripe Identity.')}
                                data-testid="verify-identity-btn"
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Verify ID
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Insurance Status - Enhanced */}
                    <div className={`p-4 rounded-xl border transition-colors ${profile.is_insured ? 'bg-green-500/10 border-green-500/30' : 'bg-muted/50 border-border'}`}>
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${profile.is_insured ? 'bg-green-500/20' : 'bg-muted'}`}>
                          <ShieldCheck className={`h-5 w-5 ${profile.is_insured ? 'text-green-400' : 'text-muted-foreground'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">Insured Professional</p>
                            <Switch
                              checked={profile.is_insured}
                              onCheckedChange={(checked) => setProfile({ ...profile, is_insured: checked })}
                              data-testid="insured-toggle"
                            />
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {profile.is_insured 
                              ? 'An "Insured" badge is displayed on your profile. Bookers trust insured professionals more!'
                              : 'Toggle on to display an "Insured" badge. Get coverage from our recommended partners below.'}
                          </p>
                          
                          {/* Recommended Insurance Partners */}
                          {!profile.is_insured && (
                            <div className="mt-4 space-y-3">
                              <p className="text-xs font-medium text-muted-foreground">Recommended Insurance Partners:</p>
                              <div className="grid gap-2">
                                <a 
                                  href="https://getflip.com" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 hover:border-emerald-500/40 transition-colors"
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                    <div>
                                      <p className="text-sm font-medium">Flip Insurance</p>
                                      <p className="text-xs text-muted-foreground">Best for freelancers & gig workers</p>
                                    </div>
                                  </div>
                                  <Badge variant="secondary" className="text-xs">Recommended</Badge>
                                </a>
                                <a 
                                  href="https://thimble.com" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-between p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 hover:border-blue-500/40 transition-colors"
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                                    <div>
                                      <p className="text-sm font-medium">Thimble</p>
                                      <p className="text-xs text-muted-foreground">On-demand liability insurance</p>
                                    </div>
                                  </div>
                                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                                </a>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2 p-2 rounded bg-muted/50">
                                <span className="font-medium">Disclaimer:</span> Bookd is not affiliated with these providers. Links are recommendations only. 
                                Please review provider terms before purchasing. See our{' '}
                                <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link> for full details.
                              </p>
                            </div>
                          )}
                          
                          {profile.is_insured && (
                            <div className="mt-3 flex items-center gap-2 p-2 rounded-lg bg-green-500/10">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <p className="text-xs text-green-600">Your "Insured" badge is visible to Bookers</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Repeat Client Discount */}
                    <div className="p-4 rounded-xl border border-border bg-muted/30">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-primary/20">
                          <DollarSign className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">Repeat Client Discount</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                Offer returning clients a discount to encourage repeat bookings
                              </p>
                            </div>
                            <Switch
                              checked={profile.repeat_client_discount?.enabled || false}
                              onCheckedChange={(checked) => setProfile({
                                ...profile,
                                repeat_client_discount: {
                                  ...profile.repeat_client_discount,
                                  enabled: checked,
                                  discount_percent: profile.repeat_client_discount?.discount_percent || 10,
                                  min_previous_bookings: profile.repeat_client_discount?.min_previous_bookings || 1
                                }
                              })}
                              data-testid="repeat-discount-toggle"
                            />
                          </div>
                          
                          {profile.repeat_client_discount?.enabled && (
                            <div className="grid sm:grid-cols-2 gap-4 pt-2">
                              <div className="space-y-2">
                                <Label className="text-sm">Discount Percentage</Label>
                                <Select
                                  value={String(profile.repeat_client_discount?.discount_percent || 10)}
                                  onValueChange={(val) => setProfile({
                                    ...profile,
                                    repeat_client_discount: {
                                      ...profile.repeat_client_discount,
                                      discount_percent: parseInt(val)
                                    }
                                  })}
                                >
                                  <SelectTrigger className="rounded-xl">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="5">5% off</SelectItem>
                                    <SelectItem value="10">10% off</SelectItem>
                                    <SelectItem value="15">15% off</SelectItem>
                                    <SelectItem value="20">20% off</SelectItem>
                                    <SelectItem value="25">25% off</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Min. Previous Bookings</Label>
                                <Select
                                  value={String(profile.repeat_client_discount?.min_previous_bookings || 1)}
                                  onValueChange={(val) => setProfile({
                                    ...profile,
                                    repeat_client_discount: {
                                      ...profile.repeat_client_discount,
                                      min_previous_bookings: parseInt(val)
                                    }
                                  })}
                                >
                                  <SelectTrigger className="rounded-xl">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="1">1 booking</SelectItem>
                                    <SelectItem value="2">2 bookings</SelectItem>
                                    <SelectItem value="3">3 bookings</SelectItem>
                                    <SelectItem value="5">5 bookings</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}
                          
                          {profile.repeat_client_discount?.enabled && (
                            <p className="text-xs text-primary bg-primary/10 p-2 rounded-lg">
                              Returning clients who have booked {profile.repeat_client_discount?.min_previous_bookings || 1}+ time(s) 
                              will see a {profile.repeat_client_discount?.discount_percent || 10}% discount on your services
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Cancellation Policy */}
                    <div className="space-y-2">
                      <Label>Cancellation Policy</Label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: 'flexible', label: 'Flexible', desc: 'Full refund 24h before' },
                          { value: 'moderate', label: 'Moderate', desc: '50% refund 48h before' },
                          { value: 'strict', label: 'Strict', desc: 'No refund within 7 days' }
                        ].map(policy => (
                          <button
                            key={policy.value}
                            type="button"
                            onClick={() => setProfile({ ...profile, cancellation_policy: policy.value })}
                            className={`p-3 rounded-xl border text-left transition-colors ${
                              profile.cancellation_policy === policy.value
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:border-primary/50'
                            }`}
                            data-testid={`policy-${policy.value}`}
                          >
                            <p className="font-medium text-sm">{policy.label}</p>
                            <p className="text-xs text-muted-foreground mt-1">{policy.desc}</p>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        See full{' '}
                        <Link to="/cancellation-policy" className="text-primary hover:underline">
                          cancellation and fee policy
                        </Link>
                        . An 8% service fee applies to all bookings.
                      </p>
                    </div>
                    
                    <Separator />
                    
                    {/* Profile Preview Section */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-base font-semibold">Profile Preview</Label>
                          <p className="text-sm text-muted-foreground">See how your profile appears to Bookers</p>
                        </div>
                        <Button
                          variant={showProfilePreview ? "default" : "outline"}
                          onClick={() => setShowProfilePreview(!showProfilePreview)}
                          className="rounded-xl"
                          data-testid="toggle-preview-btn"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          {showProfilePreview ? 'Hide Preview' : 'Show Preview'}
                        </Button>
                      </div>
                      
                      {showProfilePreview && (
                        <ProfilePreview 
                          profile={{
                            ...user,
                            ...profile,
                            main_category: profile.main_category,
                            additional_categories: profile.additional_categories
                          }}
                          categoryDetails={categoryDetails}
                        />
                      )}
                    </div>
                  </>
                )}

                <Button onClick={handleSave} disabled={loading} className="rounded-xl" data-testid="save-profile-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Portfolio Tab */}
          {isTalent && (
            <TabsContent value="portfolio">
              <Card>
                <CardHeader>
                  <CardTitle>Portfolio</CardTitle>
                  <CardDescription>Showcase your work with images, videos, and other files</CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  {/* Images */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-semibold">Portfolio Images</Label>
                        <p className="text-sm text-muted-foreground">Upload photos of your work</p>
                      </div>
                      <label className="cursor-pointer">
                        <Button variant="outline" className="rounded-xl" asChild disabled={loading}>
                          <span>
                            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                            Upload Images
                          </span>
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handlePortfolioImageUpload}
                          className="hidden"
                          data-testid="portfolio-image-upload"
                        />
                      </label>
                    </div>
                    
                    {/* Image Grid with Drag-and-Drop Reordering */}
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={profile.portfolio_images.map((_, idx) => `image-${idx}`)}
                        strategy={rectSortingStrategy}
                      >
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                          {profile.portfolio_images.map((img, idx) => (
                            <SortableImage
                              key={`image-${idx}`}
                              id={`image-${idx}`}
                              img={img}
                              idx={idx}
                              onRemove={handleRemoveImage}
                            />
                          ))}
                          {/* Add More Button */}
                          <label className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center cursor-pointer transition-colors bg-muted/30 hover:bg-muted/50">
                            <Plus className="h-8 w-8 text-muted-foreground mb-2" />
                            <span className="text-sm text-muted-foreground">Add Image</span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handlePortfolioImageUpload}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </SortableContext>
                    </DndContext>
                    
                    {profile.portfolio_images.length > 1 && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <GripVertical className="h-3 w-3" />
                        Drag images to reorder. First image appears as your primary photo.
                      </p>
                    )}
                    
                    {/* URL Input as Alternative */}
                    <div className="flex gap-2 pt-2">
                      <Input
                        placeholder="Or paste image URL..."
                        value={newImage}
                        onChange={(e) => setNewImage(e.target.value)}
                        className="rounded-xl text-sm"
                        data-testid="add-image-input"
                      />
                      <Button onClick={addPortfolioImage} variant="outline" className="rounded-xl shrink-0" data-testid="add-image-btn">
                        <Plus className="h-4 w-4 mr-1" />
                        Add URL
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  {/* Videos */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-semibold">Portfolio Videos</Label>
                      <p className="text-sm text-muted-foreground">Add links to your video content (YouTube, Vimeo, etc.)</p>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Paste video URL (YouTube, Vimeo, TikTok)..."
                        value={newVideo}
                        onChange={(e) => setNewVideo(e.target.value)}
                        className="rounded-xl"
                        data-testid="add-video-input"
                      />
                      <Button onClick={addPortfolioVideo} className="rounded-xl" data-testid="add-video-btn">
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {profile.portfolio_videos.map((video, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-3 rounded-xl bg-muted/50">
                          <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                          <a 
                            href={video} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex-1 truncate text-sm text-primary hover:underline"
                          >
                            {video}
                          </a>
                          <button
                            onClick={() => setProfile({
                              ...profile,
                              portfolio_videos: profile.portfolio_videos.filter((_, i) => i !== idx)
                            })}
                            className="text-destructive hover:text-destructive/80 p-1"
                            data-testid={`remove-video-${idx}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {profile.portfolio_videos.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No videos added yet. Paste a URL above to add one.
                        </p>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Certifications / Other Files */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-semibold">Other (Certifications, Licenses, Awards)</Label>
                        <p className="text-sm text-muted-foreground">Upload documents or add certification names</p>
                      </div>
                      <label className="cursor-pointer">
                        <Button variant="outline" className="rounded-xl" asChild disabled={loading}>
                          <span>
                            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                            Upload File
                          </span>
                        </Button>
                        <input
                          type="file"
                          accept="image/*,.pdf,.doc,.docx"
                          onChange={handleCertificationUpload}
                          className="hidden"
                          data-testid="certification-upload"
                        />
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add certification name (e.g., 'CPR Certified', 'Licensed Barber')..."
                        value={newCert}
                        onChange={(e) => setNewCert(e.target.value)}
                        className="rounded-xl"
                        data-testid="add-cert-input"
                      />
                      <Button onClick={addCertification} className="rounded-xl" data-testid="add-cert-btn">
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.certifications.map((cert, idx) => (
                        <Badge key={idx} variant="secondary" className="px-3 py-1.5 text-sm">
                          <BadgeCheck className="h-3 w-3 mr-1.5" />
                          {cert}
                          <button
                            onClick={() => setProfile({
                              ...profile,
                              certifications: profile.certifications.filter((_, i) => i !== idx)
                            })}
                            className="ml-2 hover:text-destructive"
                            data-testid={`remove-cert-${idx}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                      {profile.certifications.length === 0 && (
                        <p className="text-sm text-muted-foreground py-2">
                          No certifications added yet. Add them above.
                        </p>
                      )}
                    </div>
                  </div>

                  <Button onClick={handleSave} disabled={loading} className="rounded-xl w-full" data-testid="save-portfolio-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save Portfolio
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}
          {/* Social Tab */}
          <TabsContent value="social">
            <Card>
              <CardHeader>
                <CardTitle>Social Links</CardTitle>
                <CardDescription>Connect your social media profiles and showcase your presence</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/username' },
                  { key: 'tiktok', label: 'TikTok', icon: Globe, placeholder: 'https://tiktok.com/@username' },
                  { key: 'twitter', label: 'Twitter/X', icon: Twitter, placeholder: 'https://twitter.com/username' },
                  { key: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@channel' },
                  { key: 'spotify', label: 'Spotify', icon: Globe, placeholder: 'https://open.spotify.com/artist/...' },
                  { key: 'soundcloud', label: 'SoundCloud', icon: Globe, placeholder: 'https://soundcloud.com/username' },
                  { key: 'discord', label: 'Discord', icon: Globe, placeholder: 'https://discord.gg/invite-code' },
                  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/username' },
                  { key: 'behance', label: 'Behance', icon: Globe, placeholder: 'https://behance.net/username' },
                  { key: 'dribbble', label: 'Dribbble', icon: Globe, placeholder: 'https://dribbble.com/username' },
                  { key: 'twitch', label: 'Twitch', icon: Globe, placeholder: 'https://twitch.tv/username' },
                  { key: 'website', label: 'Personal Website', icon: Globe, placeholder: 'https://yourwebsite.com' },
                  { key: 'custom1', label: 'Custom Link 1', icon: LinkIcon, placeholder: 'https://...' },
                  { key: 'custom2', label: 'Custom Link 2', icon: LinkIcon, placeholder: 'https://...' },
                ].map(({ key, label, icon: Icon, placeholder }) => (
                  <div key={key} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">{label}</Label>
                      <Input
                        placeholder={placeholder}
                        value={profile.social_links[key] || ''}
                        onChange={(e) => updateSocialLink(key, e.target.value)}
                        className="rounded-xl"
                        data-testid={`social-${key}-input`}
                      />
                    </div>
                  </div>
                ))}

                <Button onClick={handleSave} disabled={loading} className="rounded-xl" data-testid="save-social-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Links
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payouts Tab (Bookees Only) */}
          {(roles.includes('bookee') || isTalent) && (
            <TabsContent value="payouts">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="h-5 w-5" />
                    Payout Settings
                  </CardTitle>
                  <CardDescription>Manage how you receive payments for your services</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Stripe Connect Status */}
                  <div className="p-4 rounded-xl bg-muted/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Stripe Payouts
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {connectStatus?.status === 'active' 
                            ? 'Your payouts are set up and ready to receive payments'
                            : connectStatus?.status === 'pending'
                            ? 'Complete your Stripe onboarding to start receiving payouts'
                            : 'Set up Stripe to receive automatic payouts for bookings'}
                        </p>
                      </div>
                      {connectStatus?.status === 'active' ? (
                        <Badge className="bg-green-500/20 text-green-500">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      ) : connectStatus?.status === 'pending' ? (
                        <Badge className="bg-yellow-500/20 text-yellow-500">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not Set Up</Badge>
                      )}
                    </div>
                    
                    <div className="mt-4">
                      {connectStatus?.status === 'active' ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 rounded-lg bg-background">
                              <p className="text-sm text-muted-foreground">Available Balance</p>
                              <p className="text-xl font-bold text-green-500">${connectBalance.available.toFixed(2)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-background">
                              <p className="text-sm text-muted-foreground">Pending</p>
                              <p className="text-xl font-bold">${connectBalance.pending.toFixed(2)}</p>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            className="w-full rounded-xl"
                            onClick={handleViewStripeDashboard}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View Stripe Dashboard
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          onClick={handleSetupPayouts}
                          disabled={connectLoading}
                          className="w-full rounded-xl btn-glow"
                        >
                          {connectLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Setting up...
                            </>
                          ) : (
                            <>
                              <DollarSign className="h-4 w-4 mr-2" />
                              {connectStatus?.status === 'pending' ? 'Complete Setup' : 'Set Up Payouts'}
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Payout Schedule Preference */}
                  {connectStatus?.status === 'active' && (
                    <div className="p-4 rounded-xl border border-border/50 space-y-4">
                      <div>
                        <h4 className="font-medium mb-1">Payout Schedule</h4>
                        <p className="text-sm text-muted-foreground">
                          Choose how often you want to receive your earnings
                        </p>
                      </div>
                      <RadioGroup
                        defaultValue="automatic"
                        className="space-y-3"
                      >
                        <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/50 transition-colors">
                          <RadioGroupItem value="automatic" id="payout-automatic" />
                          <div className="flex-1">
                            <Label htmlFor="payout-automatic" className="cursor-pointer font-medium">
                              Automatic (Recommended)
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Funds transfer to your bank 2 days after each completed service
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/50 transition-colors">
                          <RadioGroupItem value="weekly" id="payout-weekly" />
                          <div className="flex-1">
                            <Label htmlFor="payout-weekly" className="cursor-pointer font-medium">
                              Weekly
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              All eligible earnings transfer every Friday
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/50 transition-colors">
                          <RadioGroupItem value="monthly" id="payout-monthly" />
                          <div className="flex-1">
                            <Label htmlFor="payout-monthly" className="cursor-pointer font-medium">
                              Monthly
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              All eligible earnings transfer on the 1st of each month
                            </p>
                          </div>
                        </div>
                      </RadioGroup>
                      <p className="text-xs text-muted-foreground">
                        Note: Stripe processes payouts in 1-2 business days after release.
                      </p>
                    </div>
                  )}

                  {/* Payout Info */}
                  <div className="p-4 rounded-xl border border-border/50">
                    <h4 className="font-medium mb-2">How Payouts Work</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                        Payments are held securely until 2 days after service completion
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                        An 8% platform fee is deducted from your earnings
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                        Funds are automatically transferred to your bank account
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Account Tab */}
          <TabsContent value="account">
            <Card>
              <CardHeader>
                <CardTitle>Account Settings</CardTitle>
                <CardDescription>Manage your account, roles, and verification</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Role Management */}
                <div className="p-4 rounded-xl bg-muted/50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <ArrowLeftRight className="h-4 w-4" />
                        Your Roles
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {roles.length === 2 
                          ? 'You can both hire talent and offer services' 
                          : roles.includes('bookee') 
                          ? 'You offer services as a Bookee'
                          : 'You hire talent as a Booker'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {roles.map(role => (
                        <Badge 
                          key={role}
                          className={activeRole === role ? 'bg-primary text-primary-foreground' : 'bg-muted'}
                        >
                          {role === 'bookee' ? 'Bookee' : 'Booker'}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Role Actions */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {roles.length === 2 && (
                      <>
                        <Button
                          variant={activeRole === 'booker' ? 'default' : 'outline'}
                          size="sm"
                          className="rounded-full"
                          onClick={() => handleSwitchRole('booker')}
                          disabled={roleLoading || activeRole === 'booker'}
                        >
                          {roleLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                          View as Booker
                        </Button>
                        <Button
                          variant={activeRole === 'bookee' ? 'default' : 'outline'}
                          size="sm"
                          className="rounded-full"
                          onClick={() => handleSwitchRole('bookee')}
                          disabled={roleLoading || activeRole === 'bookee'}
                        >
                          {roleLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                          View as Bookee
                        </Button>
                      </>
                    )}
                    {!roles.includes('bookee') && (
                      <Button
                        onClick={() => handleAddRole('bookee')}
                        disabled={roleLoading}
                        className="rounded-full"
                      >
                        {roleLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                        Become a Bookee
                      </Button>
                    )}
                    {!roles.includes('booker') && (
                      <Button
                        onClick={() => handleAddRole('booker')}
                        disabled={roleLoading}
                        variant="outline"
                        className="rounded-full"
                      >
                        {roleLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                        Become a Booker
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Vanity URL / Screen Name */}
                <div className="p-4 rounded-xl bg-muted/50">
                  <div className="mb-3">
                    <p className="font-medium flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Profile Link
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Choose a unique username for your shareable profile URL
                    </p>
                  </div>
                  
                  <div className="flex gap-2 items-center">
                    <div className="flex-1 flex items-center bg-background rounded-lg border">
                      <span className="px-3 text-sm text-muted-foreground">{window.location.origin}/</span>
                      <Input
                        value={screenName}
                        onChange={(e) => {
                          setScreenName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
                          checkScreenName(e.target.value);
                        }}
                        placeholder="yourname"
                        className="border-0 focus-visible:ring-0"
                      />
                    </div>
                    {screenNameLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : screenNameAvailable?.available ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : screenNameAvailable?.available === false ? (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    ) : null}
                  </div>
                  
                  {screenNameAvailable && !screenNameAvailable.available && (
                    <p className="text-xs text-red-500 mt-1">{screenNameAvailable.reason}</p>
                  )}
                  
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      onClick={handleUpdateScreenName}
                      disabled={!screenNameAvailable?.available || screenNameLoading || screenName === user?.screen_name}
                      className="rounded-full"
                    >
                      Save Username
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyProfileLink}
                      className="rounded-full"
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy Link
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                  <Badge variant={user?.email_verified ? 'default' : 'secondary'}>
                    {user?.email_verified ? 'Email Verified' : 'Email Unverified'}
                  </Badge>
                </div>

                {/* ID Verification Section - Available for ALL users */}
                <div className={`p-4 rounded-xl border transition-colors ${user?.is_verified ? 'bg-blue-500/10 border-blue-500/30' : 'bg-muted/50 border-border'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${user?.is_verified ? 'bg-blue-500/20' : 'bg-muted'}`}>
                      <BadgeCheck className={`h-5 w-5 ${user?.is_verified ? 'text-blue-400' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Verified Identity</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {user?.is_verified 
                              ? 'Your identity has been verified. An "ID Verified" badge is displayed on your profile.'
                              : isTalent 
                                ? 'Verify your identity to display an "ID Verified" badge on your profile, building trust with clients.'
                                : 'Verify your identity for added security and trust. Show bookees you are who you say you are.'}
                          </p>
                        </div>
                        {user?.is_verified ? (
                          <Badge className="bg-blue-500 text-white ml-2 flex-shrink-0">
                            <BadgeCheck className="h-3 w-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-xl ml-2 flex-shrink-0"
                            onClick={() => toast.info('ID verification coming soon! This will connect to Stripe Identity.')}
                            data-testid="verify-identity-account-btn"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Verify ID
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Push Notifications Section */}
                <div className={`p-4 rounded-xl border transition-colors ${pushSubscribed ? 'bg-green-500/10 border-green-500/30' : 'bg-muted/50 border-border'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${pushSubscribed ? 'bg-green-500/20' : 'bg-muted'}`}>
                      {pushSubscribed ? (
                        <Bell className="h-5 w-5 text-green-400" />
                      ) : (
                        <BellOff className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Push Notifications</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {!pushSupported 
                              ? 'Push notifications are not supported in your browser.'
                              : pushPermission === 'denied'
                              ? 'Notifications are blocked. Please enable them in your browser settings.'
                              : pushSubscribed
                              ? 'You\'ll receive alerts for new bookings, messages, and important updates.'
                              : 'Enable push notifications to stay updated on bookings and messages.'}
                          </p>
                        </div>
                        {pushSupported && pushPermission !== 'denied' && (
                          <Switch
                            checked={pushSubscribed}
                            onCheckedChange={handleTogglePush}
                            disabled={pushLoading}
                            data-testid="push-notifications-toggle"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <Button
                  variant="outline"
                  onClick={async () => {
                    await logout();
                    navigate('/');
                  }}
                  className="rounded-xl"
                  data-testid="logout-btn"
                >
                  Sign Out
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Email Notifications
                </CardTitle>
                <CardDescription>
                  Manage which emails you receive from Bookd
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Booking Notifications */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    Booking Notifications
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                      <div className="flex-1">
                        <p className="font-medium">Booking Confirmations</p>
                        <p className="text-sm text-muted-foreground">
                          Receive an email when a booking is confirmed
                        </p>
                      </div>
                      <Switch
                        checked={emailPreferences.booking_confirmations}
                        onCheckedChange={(checked) => updateEmailPreference('booking_confirmations', checked)}
                        disabled={emailPrefsLoading}
                        data-testid="email-pref-confirmations"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                      <div className="flex-1">
                        <p className="font-medium">Booking Reminders</p>
                        <p className="text-sm text-muted-foreground">
                          Get reminded 24 hours before your booking
                        </p>
                      </div>
                      <Switch
                        checked={emailPreferences.booking_reminders}
                        onCheckedChange={(checked) => updateEmailPreference('booking_reminders', checked)}
                        disabled={emailPrefsLoading}
                        data-testid="email-pref-reminders"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Payment Notifications */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    Payment Notifications
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                      <div className="flex-1">
                        <p className="font-medium">Payment Receipts</p>
                        <p className="text-sm text-muted-foreground">
                          Receive receipts when you make a payment
                        </p>
                      </div>
                      <Switch
                        checked={emailPreferences.payment_receipts}
                        onCheckedChange={(checked) => updateEmailPreference('payment_receipts', checked)}
                        disabled={emailPrefsLoading}
                        data-testid="email-pref-receipts"
                      />
                    </div>
                    
                    {(roles.includes('bookee') || isTalent) && (
                      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                        <div className="flex-1">
                          <p className="font-medium">Payout Notifications</p>
                          <p className="text-sm text-muted-foreground">
                            Get notified when your payout is released
                          </p>
                        </div>
                        <Switch
                          checked={emailPreferences.payout_notifications}
                          onCheckedChange={(checked) => updateEmailPreference('payout_notifications', checked)}
                          disabled={emailPrefsLoading}
                          data-testid="email-pref-payouts"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Other Notifications */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    Other Notifications
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                      <div className="flex-1">
                        <p className="font-medium">Review Requests</p>
                        <p className="text-sm text-muted-foreground">
                          Get asked to leave a review after bookings
                        </p>
                      </div>
                      <Switch
                        checked={emailPreferences.review_requests}
                        onCheckedChange={(checked) => updateEmailPreference('review_requests', checked)}
                        disabled={emailPrefsLoading}
                        data-testid="email-pref-reviews"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                      <div className="flex-1">
                        <p className="font-medium">Marketing Emails</p>
                        <p className="text-sm text-muted-foreground">
                          Tips, promotions, and updates from Bookd
                        </p>
                      </div>
                      <Switch
                        checked={emailPreferences.marketing_emails}
                        onCheckedChange={(checked) => updateEmailPreference('marketing_emails', checked)}
                        disabled={emailPrefsLoading}
                        data-testid="email-pref-marketing"
                      />
                    </div>
                  </div>
                </div>

                {/* Info Note */}
                <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">Note:</strong> Essential account emails 
                    (password resets, security alerts) will always be sent regardless of these settings.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Venue Tab */}
          {roles.includes('venue') && (
            <TabsContent value="venue">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Venue Profile
                  </CardTitle>
                  <CardDescription>
                    Manage your venue listing for event spaces, studios, and rentals
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Venue Name & Description */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="venue-name">Venue Name *</Label>
                      <Input
                        id="venue-name"
                        placeholder="e.g., Studio 54 NYC"
                        value={venueForm.name}
                        onChange={(e) => setVenueForm({ ...venueForm, name: e.target.value })}
                        className="rounded-xl"
                        data-testid="venue-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="venue-type">Venue Type</Label>
                      <Select
                        value={venueForm.venue_type}
                        onValueChange={(val) => setVenueForm({ ...venueForm, venue_type: val })}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="venue-type-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {venueMetadata.venue_types.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="venue-description">Description</Label>
                    <Textarea
                      id="venue-description"
                      placeholder="Describe your venue, its atmosphere, and what makes it special..."
                      value={venueForm.description}
                      onChange={(e) => setVenueForm({ ...venueForm, description: e.target.value })}
                      className="rounded-xl min-h-[100px]"
                      data-testid="venue-description-input"
                    />
                  </div>

                  <Separator />

                  {/* Location */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Location
                    </Label>
                    <div className="space-y-2">
                      <Label htmlFor="venue-address">Street Address *</Label>
                      <Input
                        id="venue-address"
                        placeholder="123 Main Street"
                        value={venueForm.address}
                        onChange={(e) => setVenueForm({ ...venueForm, address: e.target.value })}
                        className="rounded-xl"
                        data-testid="venue-address-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="venue-city">City</Label>
                        <Input
                          id="venue-city"
                          placeholder="New York"
                          value={venueForm.city}
                          onChange={(e) => setVenueForm({ ...venueForm, city: e.target.value })}
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="venue-state">State</Label>
                        <Input
                          id="venue-state"
                          placeholder="NY"
                          value={venueForm.state}
                          onChange={(e) => setVenueForm({ ...venueForm, state: e.target.value })}
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="venue-zip">ZIP Code</Label>
                        <Input
                          id="venue-zip"
                          placeholder="10001"
                          value={venueForm.zip_code}
                          onChange={(e) => setVenueForm({ ...venueForm, zip_code: e.target.value })}
                          className="rounded-xl"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Capacity & Pricing */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Capacity & Pricing
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="venue-capacity">Capacity</Label>
                        <Input
                          id="venue-capacity"
                          type="number"
                          placeholder="50"
                          value={venueForm.capacity}
                          onChange={(e) => setVenueForm({ ...venueForm, capacity: e.target.value })}
                          className="rounded-xl"
                        />
                        <p className="text-xs text-muted-foreground">guests</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="venue-hourly">Hourly Rate</Label>
                        <Input
                          id="venue-hourly"
                          type="number"
                          placeholder="100"
                          value={venueForm.hourly_rate}
                          onChange={(e) => setVenueForm({ ...venueForm, hourly_rate: e.target.value })}
                          className="rounded-xl"
                        />
                        <p className="text-xs text-muted-foreground">$/hour</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="venue-daily">Daily Rate</Label>
                        <Input
                          id="venue-daily"
                          type="number"
                          placeholder="800"
                          value={venueForm.daily_rate}
                          onChange={(e) => setVenueForm({ ...venueForm, daily_rate: e.target.value })}
                          className="rounded-xl"
                        />
                        <p className="text-xs text-muted-foreground">$/day</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="venue-minhours">Min. Hours</Label>
                        <Input
                          id="venue-minhours"
                          type="number"
                          placeholder="2"
                          value={venueForm.minimum_hours}
                          onChange={(e) => setVenueForm({ ...venueForm, minimum_hours: e.target.value })}
                          className="rounded-xl"
                        />
                        <p className="text-xs text-muted-foreground">minimum</p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Amenities */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">Amenities</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {venueMetadata.amenities.map((amenity) => (
                        <div
                          key={amenity}
                          onClick={() => toggleAmenity(amenity)}
                          className={`p-3 rounded-xl border cursor-pointer transition-all ${
                            venueForm.amenities.includes(amenity)
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={venueForm.amenities.includes(amenity)}
                              className="pointer-events-none"
                            />
                            <span className="text-sm">{amenity}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Discount & Availability */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-semibold">Availability</Label>
                        <p className="text-sm text-muted-foreground">Is your venue currently available for booking?</p>
                      </div>
                      <Switch
                        checked={venueForm.is_available}
                        onCheckedChange={(checked) => setVenueForm({ ...venueForm, is_available: checked })}
                        data-testid="venue-available-toggle"
                      />
                    </div>

                    <div className="p-4 rounded-xl border border-border/50 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-base font-semibold">Offer Discount</Label>
                          <p className="text-sm text-muted-foreground">Attract more bookings with a special offer</p>
                        </div>
                        <Switch
                          checked={venueForm.offers_discount}
                          onCheckedChange={(checked) => setVenueForm({ ...venueForm, offers_discount: checked })}
                          data-testid="venue-discount-toggle"
                        />
                      </div>
                      {venueForm.offers_discount && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Discount Percentage</Label>
                            <Input
                              type="number"
                              placeholder="10"
                              value={venueForm.discount_percent}
                              onChange={(e) => setVenueForm({ ...venueForm, discount_percent: e.target.value })}
                              className="rounded-xl"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Discount Description</Label>
                            <Input
                              placeholder="e.g., First-time booking"
                              value={venueForm.discount_description}
                              onChange={(e) => setVenueForm({ ...venueForm, discount_description: e.target.value })}
                              className="rounded-xl"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Venue Badges */}
                  {venueData && (
                    <div className="p-4 rounded-xl bg-muted/50 space-y-3">
                      <Label className="text-base font-semibold">Venue Badges</Label>
                      <div className="flex flex-wrap gap-2">
                        {venueData.rating > 0 && (
                          <Badge className="bg-amber-500/20 text-amber-500">
                            ⭐ {venueData.rating.toFixed(1)} Rating
                          </Badge>
                        )}
                        {venueData.review_count > 0 && (
                          <Badge variant="outline">
                            {venueData.review_count} Reviews
                          </Badge>
                        )}
                        {venueForm.is_available && (
                          <Badge className="bg-green-500/20 text-green-500">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Available
                          </Badge>
                        )}
                        {venueForm.offers_discount && (
                          <Badge className="bg-purple-500/20 text-purple-500">
                            {venueForm.discount_percent}% Off
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleSaveVenue}
                    disabled={venueLoading}
                    className="rounded-xl btn-glow"
                    data-testid="save-venue-btn"
                  >
                    {venueLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : venueData ? (
                      'Update Venue'
                    ) : (
                      'Create Venue Profile'
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Calendar Tab */}
          {(roles.includes('bookee') || isTalent) && (
            <TabsContent value="calendar">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Calendar Events
                    </span>
                    <Button
                      onClick={() => setShowEventForm(!showEventForm)}
                      className="rounded-xl"
                      data-testid="add-event-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Event
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    Manage your calendar events and share gigs with flyers
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Event Creation Form */}
                  {showEventForm && (
                    <Card className="border-primary/50 bg-primary/5">
                      <CardContent className="pt-6 space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="event-title">Event Title *</Label>
                            <Input
                              id="event-title"
                              placeholder="e.g., DJ Set at Club XYZ"
                              value={eventForm.title}
                              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                              className="rounded-xl"
                              data-testid="event-title-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="event-type">Event Type</Label>
                            <Select
                              value={eventForm.event_type}
                              onValueChange={(val) => setEventForm({ ...eventForm, event_type: val })}
                            >
                              <SelectTrigger className="rounded-xl">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="event">Event</SelectItem>
                                <SelectItem value="gig">Gig</SelectItem>
                                <SelectItem value="booking">Booking</SelectItem>
                                <SelectItem value="personal">Personal</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="event-description">Description</Label>
                          <Textarea
                            id="event-description"
                            placeholder="Describe your event..."
                            value={eventForm.description}
                            onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                            className="rounded-xl"
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="event-start">Start Date/Time *</Label>
                            <Input
                              id="event-start"
                              type="datetime-local"
                              value={eventForm.start_date}
                              onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                              className="rounded-xl"
                              data-testid="event-start-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="event-end">End Date/Time</Label>
                            <Input
                              id="event-end"
                              type="datetime-local"
                              value={eventForm.end_date}
                              onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                              className="rounded-xl"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="event-location">Location</Label>
                          <Input
                            id="event-location"
                            placeholder="Venue name or address"
                            value={eventForm.location}
                            onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                            className="rounded-xl"
                          />
                        </div>

                        {/* Flyer/Attachment Upload */}
                        <div className="space-y-2">
                          <Label>Flyers / Attachments (max 3)</Label>
                          <p className="text-sm text-muted-foreground">
                            Add flyer images to promote your event
                          </p>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Paste image URL..."
                              id="event-attachment-input"
                              className="rounded-xl"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.target.value && eventForm.attachments.length < 3) {
                                  setEventForm({
                                    ...eventForm,
                                    attachments: [...eventForm.attachments, e.target.value]
                                  });
                                  e.target.value = '';
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              className="rounded-xl"
                              onClick={() => {
                                const input = document.getElementById('event-attachment-input');
                                if (input.value && eventForm.attachments.length < 3) {
                                  setEventForm({
                                    ...eventForm,
                                    attachments: [...eventForm.attachments, input.value]
                                  });
                                  input.value = '';
                                }
                              }}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          {/* Thumbnails for attached flyers */}
                          {eventForm.attachments.length > 0 && (
                            <div className="flex gap-3 mt-3">
                              {eventForm.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img
                                    src={url}
                                    alt={`Flyer ${idx + 1}`}
                                    className="w-20 h-20 object-cover rounded-lg border border-border"
                                  />
                                  <button
                                    onClick={() => setEventForm({
                                      ...eventForm,
                                      attachments: eventForm.attachments.filter((_, i) => i !== idx)
                                    })}
                                    className="absolute -top-2 -right-2 p-1 rounded-full bg-destructive text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-2">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="event-public"
                              checked={eventForm.is_public}
                              onCheckedChange={(checked) => setEventForm({ ...eventForm, is_public: checked })}
                            />
                            <Label htmlFor="event-public" className="text-sm cursor-pointer">
                              Make this event public (visible on platform calendar)
                            </Label>
                          </div>
                        </div>

                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={handleCreateEvent}
                            className="rounded-xl btn-glow"
                            data-testid="create-event-btn"
                          >
                            Create Event
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowEventForm(false);
                              setEventForm({
                                title: '',
                                description: '',
                                start_date: '',
                                end_date: '',
                                location: '',
                                event_type: 'event',
                                attachments: [],
                                is_public: false
                              });
                            }}
                            className="rounded-xl"
                          >
                            Cancel
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Event List */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">Your Events</Label>
                    {calendarEvents.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No events yet</p>
                        <p className="text-sm">Click &ldquo;Add Event&rdquo; to create your first event</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {calendarEvents.map((event) => (
                          <Card key={event.event_id} className="border-border/50">
                            <CardContent className="p-4">
                              <div className="flex gap-4">
                                {/* Event flyer thumbnail */}
                                {event.attachments && event.attachments.length > 0 ? (
                                  <img
                                    src={event.attachments[0]}
                                    alt="Event flyer"
                                    className="w-20 h-20 object-cover rounded-lg shrink-0"
                                  />
                                ) : (
                                  <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center shrink-0">
                                    <Calendar className="h-8 w-8 text-muted-foreground" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <h4 className="font-semibold truncate">{event.title}</h4>
                                      <p className="text-sm text-muted-foreground">
                                        {new Date(event.start_date).toLocaleDateString('en-US', {
                                          weekday: 'short',
                                          month: 'short',
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit'
                                        })}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="text-xs">
                                        {event.event_type}
                                      </Badge>
                                      {event.is_public && (
                                        <Badge className="bg-green-500/20 text-green-500 text-xs">
                                          Public
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  {event.location && (
                                    <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                                      <MapPin className="h-3 w-3" />
                                      {event.location}
                                    </p>
                                  )}
                                  {event.description && (
                                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                      {event.description}
                                    </p>
                                  )}
                                  {/* Show all attachment thumbnails */}
                                  {event.attachments && event.attachments.length > 1 && (
                                    <div className="flex gap-2 mt-2">
                                      {event.attachments.slice(1).map((url, idx) => (
                                        <img
                                          key={idx}
                                          src={url}
                                          alt={`Flyer ${idx + 2}`}
                                          className="w-12 h-12 object-cover rounded-md border border-border cursor-pointer hover:opacity-80"
                                          onClick={() => window.open(url, '_blank')}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteEvent(event.event_id)}
                                  className="text-destructive hover:text-destructive shrink-0"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Boost Tab - Promoted Listings */}
          {(roles.includes('bookee') || isTalent) && (
            <TabsContent value="boost">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Rocket className="h-5 w-5 text-primary" />
                    Boost Your Profile
                  </CardTitle>
                  <CardDescription>
                    Get a quick visibility spike with short-term profile boosts
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Current Boost Status */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/20">
                          <Zap className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">Boost Status</p>
                          <p className="text-sm text-muted-foreground">
                            {boostStatus.is_boosted 
                              ? `Active until ${boostStatus.boost_details?.end_date ? new Date(boostStatus.boost_details.end_date).toLocaleDateString() : 'soon'}`
                              : 'Not currently boosted'}
                          </p>
                        </div>
                      </div>
                      {boostStatus.is_boosted && (
                        <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Boost Plans - Short term visibility */}
                  <div className="space-y-4">
                    <h3 className="font-semibold">Choose a Boost Duration</h3>
                    <p className="text-sm text-muted-foreground">Quick visibility boosts to get more eyes on your profile</p>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {/* 1 Day Boost */}
                      <div className="p-4 rounded-xl border border-border hover:border-primary/50 transition-colors group">
                        <div className="text-center mb-3">
                          <p className="text-2xl font-bold text-primary">$5</p>
                          <p className="text-sm text-muted-foreground">1 Day</p>
                        </div>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Top of search results
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            "Boosted" badge
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            24 hours visibility
                          </li>
                        </ul>
                        <Button 
                          className="w-full mt-4 rounded-xl" 
                          variant="outline"
                          disabled={boostLoading || boostStatus.is_boosted}
                          onClick={() => handlePurchaseBoost(1, 'standard')}
                          data-testid="boost-1-day"
                        >
                          {boostLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Boost 1 Day'}
                        </Button>
                      </div>

                      {/* 3 Day Boost - Popular */}
                      <div className="p-4 rounded-xl border-2 border-primary bg-primary/5 relative">
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white">
                          Most Popular
                        </Badge>
                        <div className="text-center mb-3 pt-2">
                          <p className="text-2xl font-bold text-primary">$10</p>
                          <p className="text-sm text-muted-foreground">3 Days</p>
                          <p className="text-xs text-green-500">Save 33%</p>
                        </div>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Top of search results
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            "Boosted" badge
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Featured in Explore
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            3 days visibility
                          </li>
                        </ul>
                        <Button 
                          className="w-full mt-4 rounded-xl"
                          disabled={boostLoading || boostStatus.is_boosted}
                          onClick={() => handlePurchaseBoost(3, 'standard')}
                          data-testid="boost-3-days"
                        >
                          {boostLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Boost 3 Days'}
                        </Button>
                      </div>

                      {/* 1 Week Boost */}
                      <div className="p-4 rounded-xl border border-border hover:border-primary/50 transition-colors">
                        <div className="text-center mb-3">
                          <p className="text-2xl font-bold text-primary">$15</p>
                          <p className="text-sm text-muted-foreground">1 Week</p>
                          <p className="text-xs text-green-500">Best value - Save 57%</p>
                        </div>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Top of search results
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            "Boosted" badge
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Featured in Explore
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Calendar spotlight
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            7 days visibility
                          </li>
                        </ul>
                        <Button 
                          className="w-full mt-4 rounded-xl" 
                          variant="outline"
                          disabled={boostLoading || boostStatus.is_boosted}
                          onClick={() => handlePurchaseBoost(7, 'standard')}
                          data-testid="boost-7-days"
                        >
                          {boostLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Boost 1 Week'}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="p-4 rounded-xl bg-muted/30 border border-border">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Star className="h-4 w-4 text-yellow-500" />
                      Why Boost Your Profile?
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-start gap-2">
                        <TrendingUp className="h-4 w-4 text-primary mt-0.5" />
                        <span>Boosted profiles get 3x more views on average</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Users className="h-4 w-4 text-primary mt-0.5" />
                        <span>Appear first when clients search your category</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Zap className="h-4 w-4 text-primary mt-0.5" />
                        <span>Stand out with a "Boosted" badge on your profile</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <DollarSign className="h-4 w-4 text-primary mt-0.5" />
                        <span>Perfect for upcoming events or slow periods</span>
                      </div>
                    </div>
                  </div>

                  {/* Upsell to Subscription */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-violet-300">Want ongoing visibility?</p>
                        <p className="text-sm text-violet-400/80">Upgrade to Pro for continuous benefits + lower fees</p>
                      </div>
                      <Button 
                        variant="outline" 
                        className="rounded-full border-violet-500/50 text-violet-400 hover:bg-violet-500/20"
                        onClick={() => navigate('/subscriptions')}
                      >
                        View Plans
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;
